import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    PhoneOff, MicOff, Mic, User, MessageSquare, Send, X, ChevronDown, Users, Share2, Copy, Check, UserX, DoorOpen, Phone, Ban,
    Ghost, Mic2, Sparkles, Waves, Moon, Wind, Skull, CircleDashed, Dna, Rocket, Atom, Cpu, Sun, Zap
} from 'lucide-react';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { doc, onSnapshot, updateDoc, collection, addDoc, getDocs, deleteDoc, getDoc, query, orderBy, serverTimestamp, arrayRemove, arrayUnion, deleteField } from 'firebase/firestore';

const isDesktopDevice = () => typeof window !== 'undefined' && window.innerWidth >= 640;
import sparkLogo from '../assets/spark-logo.png';
import * as Tone from 'tone';
import { useIdentity } from '../context/IdentityContext';
import { useRightPanel } from '../context/RightPanelContext';

// Map icon names to Lucide components (premium upgrade)
const VoiceIcons = {
    Ghost, Mic2, Sparkles, Waves, Moon, Wind, Skull, CircleDashed, Dna, Rocket, Atom, Cpu, Sun, Zap, Mic
};

// Deep STUN/TURN Servers for absolute Symmetric NAT Traversal
const baseIceServers = [
    { urls: [
        'stun:stun.l.google.com:19302', 
        'stun:stun1.l.google.com:19302',
        'stun:stun2.l.google.com:19302',
        'stun:stun3.l.google.com:19302',
        'stun:stun4.l.google.com:19302'
    ]},
    { urls: [
        'stun:global.stun.twilio.com:3478',
        'stun:stun.cloudflare.com:3478',
        'stun:stun.services.mozilla.com'
    ]},
    { urls: "stun:stun.relay.metered.ca:80" }
];

if (process.env.REACT_APP_METERED_TURN_USERNAME && process.env.REACT_APP_METERED_TURN_CREDENTIAL) {
    baseIceServers.push({ urls: "turn:standard.relay.metered.ca:443", username: process.env.REACT_APP_METERED_TURN_USERNAME, credential: process.env.REACT_APP_METERED_TURN_CREDENTIAL });
    baseIceServers.push({ urls: "turns:standard.relay.metered.ca:443?transport=tcp", username: process.env.REACT_APP_METERED_TURN_USERNAME, credential: process.env.REACT_APP_METERED_TURN_CREDENTIAL });
}

const servers = {
    iceServers: baseIceServers,
    iceTransportPolicy: "all"
};

// ==================== VOICE PRESETS (same as group) ====================
const VOICE_CATEGORIES = [
    { name: 'Mysterious', voices: [
        { id: 'phantom', name: 'Phantom', icon: 'Ghost', color: '#BD00FF', description: '(Ghostly voice)', effects: { pitchShift: -3, filterType: 'highpass', filterFreq: 800, distortion: 0, reverbWet: 0.5, chorusWet: 0.3 } },
        { id: 'whisper', name: 'Whisper', icon: 'Mic2', color: '#A9A9A9', description: '(ASMR whisper)', effects: { pitchShift: -1, filterType: 'highpass', filterFreq: 2000, distortion: 0, reverbWet: 0.2, chorusWet: 0 } },
        // { id: 'oracle', name: 'Oracle', icon: 'Sparkles', color: '#7B68EE', description: '(Mystical echo)', effects: { pitchShift: -4, filterType: 'bandpass', filterFreq: 700, distortion: 0.15, reverbWet: 0.7, chorusWet: 0.5 } },
    ]},
    { name: 'Female', voices: [
        { id: 'siren', name: 'Siren', icon: 'Waves', color: '#FF69B4', description: '(Smooth feminine)', effects: { pitchShift: 8, filterType: 'highpass', filterFreq: 1200, distortion: 0, reverbWet: 0.15, chorusWet: 0.25 } },
        { id: 'luna', name: 'Luna', icon: 'Moon', color: '#DDA0DD', description: '(Sweet girl)', effects: { pitchShift: 6, filterType: 'highpass', filterFreq: 900, distortion: 0, reverbWet: 0.1, chorusWet: 0.15 } },
        { id: 'nymph', name: 'Nymph', icon: 'Wind', color: '#E0B0FF', description: '(Anime girl)', effects: { pitchShift: 11, filterType: 'highpass', filterFreq: 1500, distortion: 0, reverbWet: 0.1, chorusWet: 0.35 } },
    ]},
    /* { name: 'Dark', voices: [
        // { id: 'deep-shadow', name: 'Deep Shadow', icon: 'Skull', color: '#0BD9E5', description: '(Deep villain)', effects: { pitchShift: -4, filterType: 'lowpass', filterFreq: 1200, distortion: 0.05, reverbWet: 0.1, chorusWet: 0 } },
        // { id: 'void', name: 'Void', icon: 'CircleDashed', color: '#FF4444', description: '(Ultra-deep)', effects: { pitchShift: -6, filterType: 'lowpass', filterFreq: 1000, distortion: 0.1, reverbWet: 0.2, chorusWet: 0 } },
        // { id: 'beast', name: 'Beast', icon: 'Dna', color: '#8B0000', description: '(Creature growl)', effects: { pitchShift: -7, filterType: 'lowpass', filterFreq: 1200, distortion: 0.4, reverbWet: 0.3, chorusWet: 0 } },
    ]}, */
    /* { name: 'Cosmic', voices: [
        // { id: 'interstellar', name: 'Interstellar', icon: 'Rocket', color: '#BD00FF', description: '(Space explorer)', effects: { pitchShift: 2, filterType: 'highpass', filterFreq: 1200, reverbWet: 0.8, chorusWet: 0.4 } },
        // { id: 'nebula', name: 'Nebula', icon: 'Atom', color: '#00D4FF', description: '(Floating gas)', effects: { pitchShift: -2, filterType: 'bandpass', filterFreq: 1000, reverbWet: 0.6, chorusWet: 0.7 } },
    ]}, */
    { name: 'Digital', voices: [
        { id: 'cyborg', name: 'Cyborg', icon: 'Cpu', color: '#00FF88', description: '(Metallic)', effects: { pitchShift: -2, filterType: 'bandpass', filterFreq: 1200, distortion: 0.55, reverbWet: 0.15, chorusWet: 0.3 } },
        { id: 'ethereal', name: 'Ethereal', icon: 'Sun', color: '#FFD700', description: '(Angelic)', effects: { pitchShift: 5, filterType: 'highpass', filterFreq: 1200, distortion: 0, reverbWet: 0.3, chorusWet: 0.5 } },
    ]},
    { name: 'Fun', voices: [
        { id: 'helium', name: 'Helium', icon: 'Zap', color: '#00CED1', description: '(Chipmunk)', effects: { pitchShift: 12, filterType: 'highpass', filterFreq: 1600, distortion: 0, reverbWet: 0, chorusWet: 0.1 } },
        { id: 'original', name: 'Original', icon: 'Mic', color: '#FFFFFF', description: '(Real voice)', effects: null },
    ]},
];

const ALL_VOICE_PRESETS = VOICE_CATEGORIES.flatMap(cat => cat.voices);

// Pool for INITIAL random assignment only (High Quality categories)
const RANDOM_ASSIGNMENT_POOL = ALL_VOICE_PRESETS.filter(v => {
    const category = VOICE_CATEGORIES.find(cat => cat.voices.some(voice => voice.id === v.id));
    return v.effects !== null && 
           v.id !== 'original' && 
           category && ['Mysterious', 'Female', 'Cosmic', 'Fun'].includes(category.name);
});

function getCryptoRandomIndex(maxExclusive) {
    if (maxExclusive <= 1) return 0;
    try {
        const arr = new Uint32Array(1);
        window.crypto.getRandomValues(arr);
        return arr[0] % maxExclusive;
    } catch {
        return Math.floor(Math.random() * maxExclusive);
    }
}

function getRandomVoice(storageKey = 'spark:lastVoiceIds:custom') {
    if (RANDOM_ASSIGNMENT_POOL.length === 0) return ALL_VOICE_PRESETS.find(v => v.id === 'original') || ALL_VOICE_PRESETS[0];
    if (RANDOM_ASSIGNMENT_POOL.length === 1) return RANDOM_ASSIGNMENT_POOL[0];

    // Avoid repeating recent voices across re-joins in the same SPA session/device
    let recent = [];
    try { recent = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch {}
    if (!Array.isArray(recent)) recent = [];

    // Keep only last 3
    recent = recent.filter(Boolean).slice(-3);

    const candidates = RANDOM_ASSIGNMENT_POOL.filter(v => !recent.includes(v.id));
    const pool = candidates.length > 0 ? candidates : RANDOM_ASSIGNMENT_POOL;
    const picked = pool[getCryptoRandomIndex(pool.length)];

    try {
        const nextRecent = [...recent, picked.id].slice(-3);
        localStorage.setItem(storageKey, JSON.stringify(nextRecent));
    } catch {}

    return picked;
}

// Tone.js chain
function buildToneChain(rawStream, preset, toneNodesRef) {
    cleanupToneChain(toneNodesRef);
    if (!preset.effects) return rawStream;
    const fx = preset.effects;
    const ctx = Tone.getContext();
    const source = ctx.createMediaStreamSource(rawStream);
    const nodes = [];
    nodes.push(new Tone.PitchShift({ pitch: fx.pitchShift, windowSize: 0.1 }));
    nodes.push(new Tone.Filter({ type: fx.filterType, frequency: fx.filterFreq, rolloff: -24 }));
    if (fx.bitCrusher > 0) nodes.push(new Tone.BitCrusher(fx.bitCrusher));
    if (fx.distortion > 0) nodes.push(new Tone.Distortion(fx.distortion));
    if (fx.vibratoWet > 0) nodes.push(new Tone.Vibrato({ frequency: 5, depth: 0.3, wet: fx.vibratoWet }));
    if (fx.chorusWet > 0) nodes.push(new Tone.Chorus({ frequency: 2, delayTime: 3, depth: 0.7, wet: fx.chorusWet }).start());
    if (fx.reverbWet > 0) nodes.push(new Tone.Reverb({ decay: 1.5, wet: fx.reverbWet }));
    const destination = ctx.rawContext.createMediaStreamDestination();
    nodes.push(new Tone.Gain(1));
    const sourceGain = ctx.rawContext.createGain(); sourceGain.gain.value = 1.0;
    source.connect(sourceGain);
    const inputGain = new Tone.Gain(1);
    Tone.connect(sourceGain, inputGain);
    let chain = inputGain;
    for (const node of nodes) { chain.connect(node); chain = node; }
    Tone.connect(chain, destination);
    toneNodesRef.current = { source, sourceGain, inputGain, nodes, destination };
    return destination.stream;
}
function cleanupToneChain(ref) {
    if (ref.current) {
        const { source, sourceGain, inputGain, nodes } = ref.current;
        try { source?.disconnect(); sourceGain?.disconnect(); inputGain?.dispose(); nodes?.forEach(n => { try { n.dispose(); } catch(e){} }); } catch(e) {}
        ref.current = null;
    }
}

// ==================== COMPONENT ====================
export const CustomVoiceRoomPage = () => {
    const { identityMode } = useIdentity();
    const { setIsRightPanelOpen } = useRightPanel();
    const { roomId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [roomData, setRoomData] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [timerSeconds, setTimerSeconds] = useState(0);
    const [callActive, setCallActive] = useState(false);
    const [micReady, setMicReady] = useState(false);
    const [activeVoice, setActiveVoice] = useState(getRandomVoice());
    const [showVoicePicker, setShowVoicePicker] = useState(false);
    const [showChat, setShowChat] = useState(isDesktopDevice());
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [showInvite, setShowInvite] = useState(false);
    const [copied, setCopied] = useState(false);
    const [speakingPeers, setSpeakingPeers] = useState(new Set());
    const [connectedPeers, setConnectedPeers] = useState(new Set());
    const [kickTarget, setKickTarget] = useState(null);

    const chatEndRef = useRef(null);
    const showChatRef = useRef(isDesktopDevice());
    const localStreamRef = useRef(null);
    const processedStreamRef = useRef(null);
    const toneNodesRef = useRef(null);
    const peerConnectionsRef = useRef({});
    const remoteAudioRefs = useRef({});
    const audioAnalysersRef = useRef({});
    const candidateBufferRef = useRef({});
    const localAnalyserRef = useRef(null);
    const hasLeftRef = useRef(false);
    const kickTimeoutRef = useRef(null);

    const [currentUid, setCurrentUid] = useState(auth.currentUser?.uid);

    useEffect(() => {
        const unsub = auth.onAuthStateChanged((u) => {
            if (u) setCurrentUid(u.uid);
        });
        return () => unsub();
    }, []);

    const myUid = currentUid || auth.currentUser?.uid;
    const isCreator = roomData?.createdBy === myUid;
    const roomCode = roomData?.code || location.state?.roomCode || '';
    const roomLoaded = !!roomData; // Derived boolean: flips to true once roomData arrives from Firestore

    // ==================== ROOM LISTENER ====================
    useEffect(() => {
        if (!roomId) return;
        const unsub = onSnapshot(doc(db, 'customVoiceRooms', roomId), (snap) => {
            if (!snap.exists() || snap.data().status === 'Ended' || !snap.data().isOpen) { handleLeave(); return; }
            const data = snap.data();
            setRoomData(data);
            
            const activeUid = myUid || auth.currentUser?.uid;

            // Handle missing from room (give grace period and auto-register authenticated direct link joins)
            if (!data.participantUids?.includes(activeUid)) {
                if (activeUid && (data.participantUids?.length || 0) < (data.maxSize || 8)) {
                    updateDoc(doc(db, 'customVoiceRooms', roomId), {
                        participantUids: arrayUnion(activeUid),
                        [`participantInfo.${activeUid}`]: {
                            alias: getRandomVoice().name,
                            college: 'Unknown',
                            voiceEffect: null,
                            muted: false
                        }
                    }).catch(() => {});
                    return;
                }
                if (activeUid && !kickTimeoutRef.current) {
                    kickTimeoutRef.current = setTimeout(() => {
                        handleLeave();
                    }, 3500);
                }
                return;
            } else {
                if (kickTimeoutRef.current) {
                    clearTimeout(kickTimeoutRef.current);
                    kickTimeoutRef.current = null;
                }
            }
            
            if (data.participantUids?.length >= 2) setCallActive(true);
        });
        return () => unsub();
    }, [roomId, myUid]);

    // ==================== AUTO-OPEN RIGHT PANEL CHAT (Desktop Only) ====================
    useEffect(() => {
        const isDesktop = window.innerWidth >= 640;
        if (isDesktop) {
            setIsRightPanelOpen(true);
        }
        return () => {
            setIsRightPanelOpen(false);
        };
    }, [setIsRightPanelOpen]);

    // ==================== SCREEN WAKE LOCK ====================
    useEffect(() => {
        let wakeLock = null;
        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLock = await navigator.wakeLock.request('screen');
                }
            } catch (err) {}
        };
        requestWakeLock();
        const handleVisibilityChange = async () => {
            if (wakeLock !== null && document.visibilityState === 'visible') requestWakeLock();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (wakeLock !== null) wakeLock.release().then(() => { wakeLock = null; });
        };
    }, []);

    // ==================== MESH WEBRTC (mirrors GroupVoiceRoomPage exactly) ====================
    useEffect(() => {
        if (!roomId || !roomLoaded || !myUid) return;
        let cancelled = false;

        const attachLocalAudioToPC = (pc) => {
            const stream = processedStreamRef.current;
            const track = stream?.getAudioTracks?.()?.[0];
            if (!pc || !track) return false;

            // Respect current mute state
            track.enabled = !isMuted;

            const sender = pc.getSenders().find(s => s.track?.kind === 'audio');
            if (sender) {
                if (sender.track?.id !== track.id) sender.replaceTrack(track);
                return true;
            }

            pc.addTrack(track, stream);
            return true;
        };

        // Clean up any old/stale signals addressed to me from previous sessions
        const cleanupOldSignals = async () => {
            try {
                const signalsRef = collection(db, 'customVoiceRooms', roomId, 'signals');
                const allSignals = await getDocs(signalsRef);
                const deletePromises = [];
                allSignals.forEach(signalDoc => {
                    const data = signalDoc.data();
                    // Delete all signals belonging to this user to ensure clean rejoin state
                    const isMine = (data.to === myUid || data.from === myUid);
                    if (isMine) deletePromises.push(deleteDoc(signalDoc.ref));
                });
                await Promise.all(deletePromises);
            } catch(e) { console.error("Signal cleanup error:", e); }
        };

        const initMesh = async () => {
            try {
                // Log successful connection to a custom room
                logEvent(analytics, 'room_connected', { type: 'custom_room' });
                
                await Tone.start();
                
                // Get mic if not already
                if (!localStreamRef.current) {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                    if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
                    localStreamRef.current = stream;
                    
                    const processed = buildToneChain(stream, activeVoice, toneNodesRef);
                    processedStreamRef.current = processed;

                    const audioTrack = processed.getAudioTracks()[0];
                    if (audioTrack) {
                        const localAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
                        const localSource = localAudioCtx.createMediaStreamSource(new MediaStream([audioTrack]));
                        const localAnalyser = localAudioCtx.createAnalyser();
                        localAnalyser.fftSize = 64;
                        localSource.connect(localAnalyser);
                        localAnalyserRef.current = { analyser: localAnalyser, audioCtx: localAudioCtx };
                    }
                    setMicReady(true);

                    // If any PeerConnections were created early (e.g. we received an offer fast),
                    // make sure they have our outgoing audio before answering/negotiation completes.
                    for (const pc of Object.values(peerConnectionsRef.current)) {
                        try { attachLocalAudioToPC(pc); } catch (e) {}
                    }
                    
                    // Sync voice to Firestore
                    updateDoc(doc(db, 'customVoiceRooms', roomId), {
                        [`participantInfo.${myUid}.voiceEffect`]: { name: activeVoice.name, icon: activeVoice.icon, color: activeVoice.color }
                    }).catch(() => {});
                }
            } catch (e) {
                // console.error("Mic error:", e);
            }
        };

        // Clean stale signals in background, init mic instantly
        const startUp = async () => {
            cleanupOldSignals();
            if (!cancelled) await initMesh();
        };
        startUp();

        // Listen for signaling
        const signalsRef = collection(db, 'customVoiceRooms', roomId, 'signals');
        const signalQuery = query(signalsRef, orderBy('timestamp', 'asc'));
        
        const unsubSignals = onSnapshot(signalQuery, async (snapshot) => {
            if (cancelled) return;
            
            for (const change of snapshot.docChanges()) {
                if (change.type !== 'added') continue;
                const signal = change.doc.data();
                
                // Only process signals addressed to me
                if (signal.to !== myUid) continue;
                
                const fromUid = signal.from;
                
                if (signal.type === 'offer') {
                    // Someone sent us an offer — create answer
                    if (!processedStreamRef.current) {
                        await initMesh();
                    }
                    const pc = getOrCreatePC(fromUid);
                    attachLocalAudioToPC(pc);

                    try {
                        if (pc.signalingState === 'stable' && pc.currentRemoteDescription) {
                            continue;
                        }

                        await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
                        
                        // Flush buffered candidates
                        if (candidateBufferRef.current[fromUid]) {
                            for (const c of candidateBufferRef.current[fromUid]) {
                                try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch(e) {}
                            }
                            delete candidateBufferRef.current[fromUid];
                        }
                        
                        if (pc.signalingState === 'have-remote-offer') {
                            const answer = await pc.createAnswer();
                            await pc.setLocalDescription(answer);
                            
                            await addDoc(signalsRef, {
                                from: myUid,
                                to: fromUid,
                                type: 'answer',
                                data: { type: answer.type, sdp: answer.sdp },
                                timestamp: serverTimestamp()
                            });
                        }
                    } catch (err) {
                        console.warn("Handled WebRTC offer signaling error:", err);
                    }
                    
                } else if (signal.type === 'answer') {
                    const pc = peerConnectionsRef.current[fromUid];
                    if (pc && pc.signalingState === 'have-local-offer') {
                        try {
                            await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
                            
                            // Flush buffered candidates
                            if (candidateBufferRef.current[fromUid]) {
                                for (const c of candidateBufferRef.current[fromUid]) {
                                    try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch(e) {}
                                }
                                delete candidateBufferRef.current[fromUid];
                            }
                        } catch (err) {
                            console.warn("Handled WebRTC answer signaling error:", err);
                        }
                    }
                    
                } else if (signal.type === 'candidate') {
                    const pc = peerConnectionsRef.current[fromUid];
                    if (pc && pc.currentRemoteDescription) {
                        try { await pc.addIceCandidate(new RTCIceCandidate(signal.data)); } catch(e) {}
                    } else {
                        if (!candidateBufferRef.current[fromUid]) candidateBufferRef.current[fromUid] = [];
                        candidateBufferRef.current[fromUid].push(signal.data);
                    }
                }
            }
        });

        return () => { cancelled = true; unsubSignals(); };
    }, [roomId, myUid, roomLoaded]);

    // Watch for new participants joining — initiate WebRTC to them
    useEffect(() => {
        if (!roomData || !myUid || !micReady || !processedStreamRef.current) return;
        
        const participants = roomData.participantUids || [];
        const otherParticipants = participants.filter(uid => uid !== myUid);
        
        for (const remoteUid of otherParticipants) {
            if (!peerConnectionsRef.current[remoteUid]) {
                // I should initiate if my UID is "greater" (deterministic who offers)
                if (myUid > remoteUid) {
                    initiateConnectionTo(remoteUid);
                }
                // Otherwise, wait for their offer via signals
            }
        }
        
        // Clean up connections for participants who left
        for (const uid of Object.keys(peerConnectionsRef.current)) {
            if (!participants.includes(uid)) {
                // console.log("Peer left, cleaning up:", uid);
                peerConnectionsRef.current[uid]?.close();
                delete peerConnectionsRef.current[uid];
                if (remoteAudioRefs.current[uid]) {
                    remoteAudioRefs.current[uid].srcObject = null;
                    delete remoteAudioRefs.current[uid];
                }
                if (audioAnalysersRef.current[uid]) {
                    try { audioAnalysersRef.current[uid].audioCtx?.close(); } catch(e) {}
                    delete audioAnalysersRef.current[uid];
                }
                setConnectedPeers(prev => { const n = new Set(prev); n.delete(uid); return n; });
            }
        }
    }, [roomData?.participantUids, myUid]);

    // Helper: create or get a PeerConnection to a remote user
    const getOrCreatePC = useCallback((remoteUid) => {
        if (peerConnectionsRef.current[remoteUid]) return peerConnectionsRef.current[remoteUid];
        
        const pc = new RTCPeerConnection(servers);
        peerConnectionsRef.current[remoteUid] = pc;
        
        // Add local processed audio tracks
        if (processedStreamRef.current) {
            processedStreamRef.current.getTracks().forEach(track => {
                pc.addTrack(track, processedStreamRef.current);
            });
        }
        
        // Handle remote audio
        const remoteStream = new MediaStream();
        pc.ontrack = (event) => {
            event.streams[0].getTracks().forEach(track => remoteStream.addTrack(track));
            // Create or update audio element for this peer
            if (!remoteAudioRefs.current[remoteUid]) {
                const audio = new Audio();
                audio.autoplay = true;
                audio.playsInline = true;
                remoteAudioRefs.current[remoteUid] = audio;
            }
            remoteAudioRefs.current[remoteUid].srcObject = remoteStream;
            remoteAudioRefs.current[remoteUid].play().catch(() => {});
            
            // Set up audio analyser for speaking detection
            try {
                const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                const source = audioCtx.createMediaStreamSource(remoteStream);
                const analyser = audioCtx.createAnalyser();
                analyser.fftSize = 256;
                analyser.smoothingTimeConstant = 0.5;
                source.connect(analyser);
                const dataArray = new Uint8Array(analyser.frequencyBinCount);
                audioAnalysersRef.current[remoteUid] = { analyser, dataArray, audioCtx };
            } catch(e) { console.error("Analyser setup error:", e); }
        };
        
        // Connection state
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'connected') {
                // Artificial UI delay so the "Connecting" badge doesn't just flash for 50ms and look jarring
                setTimeout(() => {
                    setConnectedPeers(prev => new Set([...prev, remoteUid]));
                }, 1200);
            } else if (['failed', 'closed'].includes(pc.connectionState)) {
                setConnectedPeers(prev => { const n = new Set(prev); n.delete(remoteUid); return n; });
            }
        };

        pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === 'failed') {
                if (typeof pc.restartIce === 'function') pc.restartIce();
            }
        };
        
        // ICE candidates
        const signalsRef = collection(db, 'customVoiceRooms', roomId, 'signals');
        pc.onicecandidate = (event) => {
            if (event.candidate) {
                addDoc(signalsRef, {
                    from: myUid,
                    to: remoteUid,
                    type: 'candidate',
                    data: event.candidate.toJSON(),
                    timestamp: serverTimestamp()
                }).catch(() => {});
            }
        };
        
        return pc;
    }, [roomId, myUid]);

    // Initiate WebRTC connection to a specific peer
    const initiateConnectionTo = useCallback(async (remoteUid) => {
        // console.log("Initiating WebRTC to:", remoteUid);
        const pc = getOrCreatePC(remoteUid);
        
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        
        const signalsRef = collection(db, 'customVoiceRooms', roomId, 'signals');
        await addDoc(signalsRef, {
            from: myUid,
            to: remoteUid,
            type: 'offer',
            data: { type: offer.type, sdp: offer.sdp },
            timestamp: serverTimestamp()
        });
    }, [roomId, myUid, getOrCreatePC]);

    // Chat
    useEffect(() => {
        if (!roomId) return;
        const q = query(collection(db, 'customVoiceRooms', roomId, 'messages'), orderBy('timestamp', 'asc'));
        let first = true;
        const unsub = onSnapshot(q, (snap) => {
            setChatMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            if (!first && !showChatRef.current) {
                const newMsgs = snap.docChanges().filter(c => c.type === 'added' && c.doc.data().sender !== myUid);
                if (newMsgs.length > 0) setUnreadCount(prev => prev + newMsgs.length);
            }
            first = false;
        });
        return () => unsub();
    }, [roomId]);

    useEffect(() => { if (showChat && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages, showChat]);

    // Timer
    useEffect(() => { let i; if (callActive) i = setInterval(() => setTimerSeconds(p => p+1), 1000); return () => clearInterval(i); }, [callActive]);

    // Speaking detection
    useEffect(() => {
        if (!callActive) return;
        const interval = setInterval(() => {
            const speaking = new Set();
            for (const [uid, d] of Object.entries(audioAnalysersRef.current)) {
                try { d.analyser.getByteFrequencyData(d.dataArray); if (d.dataArray.reduce((a,b) => a+b, 0) / d.dataArray.length > 25) speaking.add(uid); } catch(e) {}
            }
            if (localAnalyserRef.current && !isMuted) {
                try { const d = localAnalyserRef.current; d.analyser.getByteFrequencyData(d.dataArray); if (d.dataArray.reduce((a,b) => a+b, 0) / d.dataArray.length > 25) speaking.add('self'); } catch(e) {}
            }
            setSpeakingPeers(speaking);
        }, 150);
        return () => clearInterval(interval);
    }, [callActive, isMuted]);

    // ==================== HANDLERS ====================
    const switchVoice = (preset) => {
        setActiveVoice(preset); setShowVoicePicker(false);
        if (!localStreamRef.current) return;
        const newProcessed = buildToneChain(localStreamRef.current, preset, toneNodesRef);
        processedStreamRef.current = newProcessed;
        
        // Ensure new audio track respects current mute state
        newProcessed.getAudioTracks().forEach(t => { t.enabled = !isMuted; });
        
        for (const pc of Object.values(peerConnectionsRef.current)) {
            const s = pc.getSenders().find(s => s.track?.kind === 'audio');
            if (s && newProcessed.getAudioTracks().length > 0) {
                s.replaceTrack(newProcessed.getAudioTracks()[0]);
            }
        }
        if (roomId) updateDoc(doc(db, 'customVoiceRooms', roomId), { [`participantInfo.${myUid}.voiceEffect`]: { name: preset.name, icon: preset.icon, color: preset.color } }).catch(() => {});
    };

    const handleLeave = () => {
        if (hasLeftRef.current) return;
        hasLeftRef.current = true;
        
        // INSTANT NAVIGATION FIRST
        navigate('/custom-rooms');
        
        for (const pc of Object.values(peerConnectionsRef.current)) try { pc.close(); } catch(e) {}
        peerConnectionsRef.current = {};
        for (const a of Object.values(remoteAudioRefs.current)) try { a.srcObject = null; } catch(e) {}
        remoteAudioRefs.current = {};
        for (const d of Object.values(audioAnalysersRef.current)) try { d.audioCtx?.close(); } catch(e) {}
        audioAnalysersRef.current = {};
        if (localAnalyserRef.current) { try { localAnalyserRef.current.audioCtx?.close(); } catch(e) {} localAnalyserRef.current = null; }
        cleanupToneChain(toneNodesRef);
        if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
        
        if (roomId && myUid) {
            // Asynchronous cleanup (fire-and-forget)
            // 1. Remove user from room participants
            (async () => {
                try {
                    const roomRef = doc(db, 'customVoiceRooms', roomId);
                    const snap = await getDoc(roomRef);
                    if (snap.exists()) {
                        const data = snap.data();
                        const remaining = (data.participantUids || []).filter(u => u !== myUid);
                        if (remaining.length === 0) await updateDoc(roomRef, { status: 'Ended', participantUids: [] });
                        else await updateDoc(roomRef, { participantUids: arrayRemove(myUid), [`participantInfo.${myUid}`]: deleteField() });
                    }
                } catch(e) { console.error(e); }
            })();
            
            // 2. Delete personal signals
            (async () => {
                try {
                    const signalsRef = collection(db, 'customVoiceRooms', roomId, 'signals');
                    const allSignals = await getDocs(signalsRef);
                    const deletePromises = [];
                    allSignals.forEach(signalDoc => {
                        const data = signalDoc.data();
                        if (data.to === myUid || data.from === myUid) deletePromises.push(deleteDoc(signalDoc.ref));
                    });
                    await Promise.all(deletePromises);
                } catch(e) { console.error(e); }
            })();
        }
    };

    const closeRoom = async () => {
        if (!isCreator || !roomId) return;
        try { await updateDoc(doc(db, 'customVoiceRooms', roomId), { status: 'Ended', participantUids: [] }); } catch(e) {}
    };

    const kickUser = async (uid) => {
        if (!isCreator || !roomId) return;
        try {
            await updateDoc(doc(db, 'customVoiceRooms', roomId), {
                participantUids: arrayRemove(uid),
                [`participantInfo.${uid}`]: null
            });
        } catch(e) { console.error("Kick error:", e); }
        setKickTarget(null);
    };

    const toggleMute = () => {
        const m = !isMuted;
        if (localStreamRef.current) localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !m; });
        if (processedStreamRef.current) processedStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !m; });
        for (const pc of Object.values(peerConnectionsRef.current)) pc.getSenders().forEach(s => { if (s.track?.kind === 'audio') s.track.enabled = !m; });
        setIsMuted(m);
        if (roomId && myUid) updateDoc(doc(db, 'customVoiceRooms', roomId), { [`participantInfo.${myUid}.muted`]: m }).catch(() => {});
    };
    // Right Panel Sync
    useEffect(() => {
        setIsRightPanelOpen(showChat);
        return () => setIsRightPanelOpen(false);
    }, [showChat, setIsRightPanelOpen]);

    const sendChat = async () => {
        if (!chatInput.trim() || !roomId) return;
        const text = chatInput.trim(); setChatInput('');
        try {
            const myInfo = roomData?.participantInfo?.[myUid];
            await addDoc(collection(db, 'customVoiceRooms', roomId, 'messages'), { sender: myUid, senderAlias: myInfo?.alias || 'Anonymous', text, timestamp: serverTimestamp() });
        } catch(e) {}
    };

    const openChat = () => { setShowChat(true); setUnreadCount(0); showChatRef.current = true; };
    const formatTime = (s) => { const m = Math.floor(s/60); const sec = s%60; return `${m}:${sec<10?'0':''}${sec}`; };

    const copyRoomCode = () => {
        navigator.clipboard.writeText(roomCode);
        setCopied(true); setTimeout(() => setCopied(false), 2000);
    };

    // ==================== DERIVED ====================
    const participants = roomData?.participantUids || [];
    const participantInfo = roomData?.participantInfo || {};
    const myInfo = participantInfo[myUid];
    const otherParticipants = participants.filter(uid => uid !== myUid);

    const gridSlots = [];
    for (const uid of otherParticipants) gridSlots.push({ uid, info: participantInfo[uid] });
    while (gridSlots.length < 7) gridSlots.push(null);

    // Adaptive grid: show only needed rows (2 cols)
    const totalOccupied = otherParticipants.length + 1; // +1 for self
    const visibleSlots = Math.max(4, Math.ceil(totalOccupied / 2) * 2); // min 4, always even
    const slotsToShow = gridSlots.slice(0, visibleSlots - 1); // -1 for self card

    // ==================== RENDER ====================
    if (!roomData) return <div className="h-screen bg-black flex items-center justify-center"><div className="w-8 h-8 border-t-2 border-b-2 border-[#FF6600] rounded-full animate-spin" /></div>;

    if (identityMode === 'real') {
       return (
         <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
            <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
               <Phone size={32} />
            </div>
            <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">Custom Room</h2>
            <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
         </main>
       );
    }

    return (
        <main className="w-full sm:w-[700px] h-screen sm:h-auto sm:min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
            <div className="absolute inset-0 pointer-events-none z-0">
                <div className="absolute inset-0 opacity-60" style={{ background: 'radial-gradient(circle at 50% 30%, rgba(255,102,0,0.1), transparent 70%)' }} />
            </div>

            <div className="relative z-10 flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between pt-3 px-4 pb-1 relative">
                    <div className="flex items-center">
                    </div>

                    <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 min-w-0">
                        <Users size={14} className="text-[#FF6600] flex-shrink-0" />
                        <span className="text-xs font-bold text-[#FF6600] truncate">{roomData.name}</span>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <button onClick={() => setShowInvite(true)} className="p-1.5 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors">
                            <Share2 size={14} className="text-[#FF6600]" />
                        </button>
                        <span className="text-[10px] font-mono text-gray-500">{participants.length}/8</span>
                    </div>
                </div>

                {/* Timer overlay */}
                {callActive && (
                    <div className="flex justify-center py-1">
                        <div className="flex items-center gap-2 px-3 py-0.5 rounded-full bg-black/70 border border-gray-800">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                            <p className="font-mono text-gray-300 font-bold text-xs">{formatTime(timerSeconds)}</p>
                        </div>
                    </div>
                )}

                {/* Adaptive Grid */}
                <div className="flex-1 grid grid-cols-2 gap-1.5 px-2 py-1" style={{ gridTemplateRows: `repeat(${Math.ceil(visibleSlots/2)}, 1fr)` }}>
                    {slotsToShow.map((slot, i) => {
                        const isSpeaking = slot ? speakingPeers.has(slot.uid) : false;
                        return (
                            <div key={slot?.uid || `e-${i}`}
                                 className="rounded-2xl bg-gray-900/60 backdrop-blur-sm flex flex-col items-center justify-center gap-1.5 relative overflow-hidden transition-all duration-200"
                                 style={{ border: isSpeaking ? '2px solid #FF6600' : '1px solid rgba(55,65,81,0.5)', boxShadow: isSpeaking ? '0 0 25px rgba(255,102,0,0.3)' : 'none' }}>
                                {slot ? (
                                    <div className="relative">
                                        {isSpeaking && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><motion.div animate={{ scale:[1,1.3,1], opacity:[0.4,0,0.4] }} transition={{ duration:1.2, repeat:Infinity }} className="absolute w-16 h-16 border-2 border-[#FF6600] rounded-full" /></div>}
                                        <div className={`w-16 h-16 rounded-full bg-gray-800 flex flex-col items-center justify-center overflow-hidden shadow-xl transition-all ${isSpeaking ? 'border-[3px] border-[#FF6600] scale-105' : 'border-2 border-gray-600'}`}>
                                            {slot.info?.alias ? <img src={`/avatars/${slot.info.alias}.png`} alt="" className="w-full h-full object-cover" /> : <User className="text-gray-500 w-7 h-7" />}
                                            
                                            {/* Connecting / Routing Overlay */}
                                            {slot.uid !== myUid && !connectedPeers.has(slot.uid) && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[1px] rounded-full z-10 w-16 h-16">
                                                    <motion.p animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.5, repeat: Infinity }} className="text-[7px] font-bold text-white uppercase tracking-widest text-center px-0.5">
                                                        Connecting
                                                    </motion.p>
                                                </div>
                                            )}
                                        </div>
                                        {slot.info?.muted && <div className="absolute -top-0.5 -right-0.5 z-20 w-5 h-5 rounded-full bg-red-500/90 flex items-center justify-center"><MicOff size={9} className="text-white" /></div>}
                                    </div>
                                ) : (
                                    <>
                                        <div className="w-16 h-16 rounded-full border-2 border-dashed border-gray-700 flex items-center justify-center"><User className="text-gray-700 w-7 h-7" /></div>
                                        <p className="text-xs text-gray-600">Waiting...</p>
                                    </>
                                )}
                                {slot && (
                                    <>
                                        <p className="text-sm font-bold truncate max-w-full px-2">{slot.info?.alias || 'Anon'}</p>
                                        {roomData?.showCollegeNames && slot.info?.college && (
                                            <span className="text-[8px] text-gray-500 truncate max-w-full px-2 mb-1">{slot.info.college}</span>
                                        )}
                                        {slot.info?.voiceEffect && (
                                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/50 border border-gray-700">
                                                {(() => {
                                                    const Icon = VoiceIcons[slot.info.voiceEffect.icon] || User;
                                                    return <Icon size={9} style={{ color: slot.info.voiceEffect.color }} />;
                                                })()}
                                                <span className="text-[7px] font-bold uppercase" style={{ color: slot.info.voiceEffect.color }}>{slot.info.voiceEffect.name}</span>
                                            </div>
                                        )}
                                        <div className={`absolute top-2 left-2 w-2 h-2 rounded-full ${connectedPeers.has(slot.uid) ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} />
                                        {/* Kick button for creator */}
                                        {isCreator && (
                                            <button onClick={() => kickUser(slot.uid)} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-red-500 hover:text-white transition-colors group z-20 shadow-md border border-gray-700">
                                                <Ban size={12} className="text-gray-400 group-hover:text-white" />
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}

                    {/* My card */}
                    <div className="rounded-2xl bg-gray-900/60 backdrop-blur-sm flex flex-col items-center justify-center gap-1.5 relative overflow-hidden transition-all duration-200"
                         style={{ border: speakingPeers.has('self') ? '2px solid #FF6600' : '1px solid rgba(255,102,0,0.2)', boxShadow: speakingPeers.has('self') ? '0 0 25px rgba(255,102,0,0.3)' : 'none', background: 'rgba(255,102,0,0.03)' }}>
                        <div className="relative mb-1 mt-1">
                            {speakingPeers.has('self') && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><motion.div animate={{ scale:[1,1.3,1], opacity:[0.4,0,0.4] }} transition={{ duration:1.2, repeat:Infinity }} className="absolute w-16 h-16 border-2 border-[#FF6600] rounded-full" /></div>}
                            <div className={`w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden shadow-xl transition-all ${speakingPeers.has('self') ? 'border-[3px] border-[#FF6600] scale-105' : 'border-2 border-[#FF6600]/40'}`}>
                                {myInfo?.alias ? <img src={`/avatars/${myInfo.alias}.png`} alt="" className="w-full h-full object-cover" /> : <User className="text-gray-500 w-7 h-7" />}
                            </div>
                            {isMuted && <div className="absolute -top-0.5 -right-0.5 z-20 w-5 h-5 rounded-full bg-red-500/90 flex items-center justify-center"><MicOff size={9} className="text-white" /></div>}
                        </div>
                        <p className="text-sm font-bold">{myInfo?.alias || 'You'} <span className="text-[8px] text-gray-500">(YOU)</span></p>
                        {roomData?.showCollegeNames && myInfo?.college && (
                            <span className="text-[8px] text-gray-400 font-medium">{myInfo.college}</span>
                        )}
                        {isCreator && <span className="text-[7px] font-bold uppercase tracking-wider text-[#FF6600] bg-[#FF6600]/10 px-2 py-0.5 rounded-full">Host</span>}
                        {callActive && <div className="absolute top-2 left-2 w-2 h-2 rounded-full bg-green-500" />}
                    </div>
                </div>

                {/* Controls */}
                <div className="pb-8 pt-3 flex items-center justify-center gap-5 bg-gradient-to-t from-black via-black to-transparent">
                    <button onClick={toggleMute} className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${isMuted ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'}`}>
                        {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
                    </button>
                    <button onClick={openChat} className="w-12 h-12 rounded-full bg-gray-800 text-white hover:bg-gray-700 flex items-center justify-center shadow-lg relative">
                        <MessageSquare size={20} />
                        {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[8px] font-bold flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                    </button>
                    
                    <div className="flex items-center gap-4">
                        <button onClick={handleLeave} className="w-16 h-16 rounded-[2rem] bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-[0_0_25px_rgba(239,68,68,0.3)]">
                            <Phone size={28} className="rotate-[135deg]" />
                        </button>

                        <button 
                            className="w-14 h-14 rounded-full border-2 bg-gray-800 flex items-center justify-center transition-all shadow-lg hover:bg-gray-700"
                            style={{ borderColor: activeVoice.color + '80' }}
                            onClick={() => setShowVoicePicker(!showVoicePicker)}
                            title={`Change Voice: ${activeVoice.name}`}
                        >
                            {(() => {
                                const Icon = VoiceIcons[activeVoice.icon] || User;
                                return <Icon size={24} style={{ color: activeVoice.color, filter: `drop-shadow(0 0 8px ${activeVoice.color}40)` }} />;
                            })()}
                        </button>
                    </div>

                    {isCreator && (
                        <button onClick={closeRoom} className="w-12 h-12 rounded-full bg-gray-800 text-red-400 hover:bg-red-500/20 flex items-center justify-center shadow-lg" title="Close Room">
                            <DoorOpen size={20} />
                        </button>
                    )}
                </div>
            </div>

            {/* ==================== KICK CONFIRM ==================== */}
            <AnimatePresence>
                {kickTarget && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
                        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-gray-900 border border-gray-800 rounded-3xl p-6 max-w-sm w-full text-center">
                            <UserX size={32} className="text-red-500 mx-auto mb-4" />
                            <h3 className="text-lg font-bold mb-2">Kick {participantInfo[kickTarget]?.alias || 'this user'}?</h3>
                            <p className="text-sm text-gray-500 mb-6">They'll be removed from the room immediately.</p>
                            <div className="flex gap-3">
                                <button onClick={() => setKickTarget(null)} className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 font-bold text-sm">Cancel</button>
                                <button onClick={() => kickUser(kickTarget)} className="flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-600 font-bold text-sm">Kick</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ==================== INVITE OVERLAY ==================== */}
            <AnimatePresence>
                {showInvite && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center">
                        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25 }} className="bg-[#111] border-t border-gray-800 rounded-t-3xl p-6 pb-10 w-full max-w-lg">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-bold">Invite Friends</h3>
                                <button onClick={() => setShowInvite(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
                            </div>
                            <div className="bg-gray-900 rounded-2xl p-4 mb-4">
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Room Code</p>
                                <div className="flex items-center justify-between">
                                    <p className="text-2xl font-black tracking-[0.15em] text-[#FF6600]">{roomCode}</p>
                                    <button onClick={copyRoomCode} className="p-2 rounded-xl bg-gray-800 hover:bg-gray-700">
                                        {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                                    </button>
                                </div>
                            </div>
                            <div className="bg-gray-900 rounded-2xl p-4 mb-4">
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Share Link</p>
                                <p className="text-xs text-gray-400 truncate mb-3">{`${window.location.origin}/room/${roomCode}`}</p>
                                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/room/${roomCode}`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                                    className="w-full py-3 rounded-xl bg-[#FF6600] hover:bg-[#FF6600]/80 font-bold text-sm flex items-center justify-center gap-2">
                                    <Copy size={16} /> Copy Link
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ==================== VOICE PICKER ==================== */}
            <AnimatePresence>
                {showVoicePicker && (
                    <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} className="absolute bottom-0 left-0 right-0 z-50 bg-[#111] border-t border-gray-800 rounded-t-3xl p-6 pb-10">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold">Voice Effect</h3>
                            <button onClick={() => setShowVoicePicker(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
                        </div>
                        <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
                            {VOICE_CATEGORIES.map(cat => (
                                <div key={cat.name}>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2 pl-1">{cat.name}</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {cat.voices.map(preset => (
                                            <div key={preset.id} className={`flex flex-col items-center gap-1 p-2.5 rounded-2xl border transition-all cursor-pointer ${activeVoice.id === preset.id ? 'scale-[1.03]' : 'border-gray-800 bg-transparent hover:bg-white/5'}`}
                                                 style={{ borderColor: activeVoice.id === preset.id ? preset.color : undefined, backgroundColor: activeVoice.id === preset.id ? preset.color + '15' : undefined }}
                                                 onClick={() => switchVoice(preset)}>
                                                {(() => {
                                                    const Icon = VoiceIcons[preset.icon] || User;
                                                    return <Icon size={18} style={{ color: preset.color, filter: `drop-shadow(0 0 8px ${preset.color}40)` }} />;
                                                })()}
                                                <span className="text-[9px] font-bold uppercase" style={{ color: activeVoice.id === preset.id ? preset.color : '#9CA3AF' }}>{preset.name}</span>
                                                <span className="text-[7px] text-gray-500 text-center">{preset.description}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ==================== CHAT ==================== */}
            {(() => {
                const chatPanelContent = (
                    <div className="flex flex-col h-full bg-black/95 backdrop-blur-xl lg:bg-black w-full border-gray-800 lg:border-l-0">
                        {/* Mobile Chat Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-800 lg:hidden">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#FF6600]/20 border border-[#FF6600]/50 flex items-center justify-center"><Users size={14} className="text-[#FF6600]" /></div>
                                <div><p className="text-sm font-bold">{roomData?.name || 'Room'}</p><p className="text-[10px] text-[#FF6600]">{participants.length + 1} people</p></div>
                            </div>
                            <button onClick={() => setShowChat(false)} className="text-gray-500 hover:text-white p-2"><X size={20} /></button>
                        </div>

                        {/* Desktop Chat Header */}
                        <div className="hidden lg:flex items-center justify-between p-4 border-b border-gray-800">
                             <h3 className="font-extrabold text-[20px]">Room Chat</h3>
                             <button onClick={() => setShowChat(false)} className="text-gray-500 hover:text-white transition-colors p-2">
                                 <X size={20} />
                             </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {chatMessages.length === 0 && <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm"><MessageSquare size={32} className="mb-3 opacity-30" /><p>Send a message</p></div>}
                            {chatMessages.map(msg => {
                                    const isMe = msg.sender === myUid;
                                    const senderAvatar = msg.senderAlias ? `/avatars/${msg.senderAlias}.png` : null;

                                    return (
                                        <div key={msg.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`flex items-end gap-2 max-w-[85%] ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                                {/* Avatar (For incoming only) */}
                                                {!isMe && (
                                                    <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0 flex items-center justify-center overflow-hidden mb-1">
                                                        {senderAvatar ? (
                                                            <img src={senderAvatar} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                                        ) : (
                                                            <User size={12} className="text-gray-500" />
                                                        )}
                                                        {!senderAvatar && <User size={12} className="text-gray-500 absolute" style={{ zIndex: -1 }} />}
                                                    </div>
                                                )}

                                                {/* Message Content */}
                                                <div className="flex flex-col">
                                                    {!isMe && (
                                                        <p className="text-[10px] text-gray-500 font-bold mb-1 ml-2">{msg.senderAlias || 'Anonymous'}</p>
                                                    )}
                                                    <div className={`px-4 py-2.5 rounded-2xl text-sm break-words ${
                                                        isMe 
                                                            ? 'bg-[#FF6600] text-white rounded-br-sm' 
                                                            : 'bg-gray-800 text-white rounded-bl-sm'
                                                    }`}>
                                                        {msg.text}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="p-4 border-t border-gray-800">
                            <div className="flex items-center gap-3 bg-gray-900 rounded-2xl px-4 py-2 border border-gray-800 focus-within:border-[#FF6600]/50">
                                <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); }}} placeholder="Type a message..." className="flex-1 bg-transparent text-white text-sm outline-none placeholder-gray-500" autoFocus />
                                <button onClick={sendChat} disabled={!chatInput.trim()} className="text-[#FF6600] hover:text-white disabled:opacity-30"><Send size={20} /></button>
                            </div>
                        </div>
                    </div>
                );

                const portalTarget = document.getElementById('right-panel-chat-container');

                return (
                    <>
                        <div className="lg:hidden">
                            <AnimatePresence>
                                {showChat && (
                                    <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'tween', duration: 0.25 }} className="absolute inset-0 z-50 flex flex-col">
                                        {chatPanelContent}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {showChat && portalTarget && ReactDOM.createPortal(chatPanelContent, portalTarget)}
                    </>
                );
            })()}
        </main>
    );
};

export default CustomVoiceRoomPage;

